import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useSortBy } from 'react-instantsearch';
import cn from 'classnames';

import DropDown from '_components/UI/DropDown';

import { getIndex } from '@utils/algolia/analytics';
import { getPriceSortSuffix } from '@utils/algolia/priceContext';
import { usePriceGroupKey } from '@hooks/usePriceGroupKey';
import { FEATURE_FLAG_PLP_SORT_SHEET_SAFE_AREA } from '@config/feature-flag';

import styles from './PlpSortBar.module.scss';

/**
 * Sort control for the PLP bar — renders as the design-system "Dropdown Button"
 * (`secondary` variant): `Sort by: <selection>` followed by a caret.
 */
const SortSelect = ({ locale = 'en' }: { locale?: string }) => {
  const { t } = useTranslation(['searchResults']);
  const [open, setOpen] = useState(false);

  // Price sort uses the replica that ranks on this customer's effective price
  // (guest keeps `_price_*`; other tiers use `_price_<tier>_*`).
  const priceSortSuffix = getPriceSortSuffix(usePriceGroupKey());

  const { currentRefinement, options, refine } = useSortBy({
    items: [
      // The redesign calls the default relevance sort "Featured"; the legacy
      // `SortResults` widget still labels the same replica "Popularity".
      { label: t('search.sortby.featured'), value: getIndex(locale) },
      {
        label: t('search.sortby.price.asc'),
        value: getIndex(locale, `_price${priceSortSuffix}_asc`),
      },
      {
        label: t('search.sortby.price.desc'),
        value: getIndex(locale, `_price${priceSortSuffix}_desc`),
      },
      {
        label: t('search.sortby.rating'),
        value: getIndex(locale, '_reviewscore_desc'),
      },
    ],
  });

  const selectedLabel =
    options.find((option) => option.value === currentRefinement)?.label ??
    options[0]?.label;

  return (
    <DropDown.Root open={open} onOpenChange={setOpen}>
      <DropDown.Trigger variant="secondary" className={styles.sortTrigger}>
        {`${t('search.sortby.title')}: ${selectedLabel}`}
      </DropDown.Trigger>
      <DropDown.Content>
        <ul
          className={cn(styles.sortList, {
            [styles.sortListSafeAreaFix]: FEATURE_FLAG_PLP_SORT_SHEET_SAFE_AREA,
          })}
          role="listbox"
          aria-label={t('search.sortby.title')}
        >
          {options.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={option.value === currentRefinement}
                className={cn(styles.sortOption, {
                  [styles.sortOptionSelected]:
                    option.value === currentRefinement,
                })}
                onClick={() => {
                  refine(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      </DropDown.Content>
    </DropDown.Root>
  );
};

export default SortSelect;
